import { useEffect, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LayoutDashboard, Upload, ListOrdered, PiggyBank, CalendarClock, Landmark, Target, FileText, Settings, Moon, Sun, Undo2, MoreHorizontal, ShieldCheck, HelpCircle, CloudOff, Inbox } from "lucide-react";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { cn } from "../lib/utils";

export const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard" },
  { to: "/upload", label: "Upload Statements", short: "Upload", icon: Upload, id: "upload" },
  { to: "/transactions", label: "Transactions", short: "Activity", icon: ListOrdered, id: "transactions" },
  { to: "/budget", label: "Monthly Budget", short: "Budget", icon: PiggyBank, id: "budget" },
  { to: "/bills", label: "Bills", icon: CalendarClock, id: "bills" },
  { to: "/debt", label: "Debt", icon: Landmark, id: "debt" },
  { to: "/goals", label: "Savings Goals", icon: Target, id: "goals" },
  { to: "/reports", label: "Reports", icon: FileText, id: "reports" },
  { to: "/receipts", label: "Receipt Inbox", short: "Receipts", icon: Inbox, id: "receipts" },
  { to: "/settings", label: "Settings", icon: Settings, id: "settings" },
];

function NavItem({ item, onClick }) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onClick}
      data-testid={`nav-${item.id}-link`}
      className={({ isActive }) => cn("flex items-center gap-3 rounded-lg px-3 py-2.5 text-[15px] font-medium transition-colors duration-150", isActive ? "bg-primary text-primary-foreground shadow-sm" : "text-foreground/80 hover:bg-secondary")}
    >
      <Icon className="h-5 w-5" />
      {item.label}
    </NavLink>
  );
}

function TopActions() {
  const { state, update, undo, undoCount, savedAt } = useStore();
  const dark = state.settings.theme === "dark";
  const onUndo = () => {
    const l = undo();
    if (l) toast.success(`Undone: ${l}`);
  };
  return (
    <div className="flex items-center gap-1.5">
      <span className="hidden lg:flex items-center gap-1.5 text-xs text-muted-foreground mr-2" data-testid="autosave-status">
        <ShieldCheck className="h-3.5 w-3.5 text-income" /> {savedAt ? `Saved on this device · ${savedAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "Stored on this device"}
      </span>
      {!navigator.onLine && <CloudOff className="h-4 w-4 text-warning" data-testid="offline-indicator" />}
      <Button variant="ghost" size="sm" onClick={onUndo} disabled={!undoCount} data-testid="undo-button" title="Undo last change">
        <Undo2 className="h-4 w-4" /> <span className="hidden sm:inline ml-1">Undo</span>
      </Button>
      <Button variant="ghost" size="icon" onClick={() => update("Toggle theme", (d) => (d.settings.theme = dark ? "light" : "dark"))} data-testid="theme-toggle-button" title="Light / dark">
        {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
      </Button>
    </div>
  );
}

export default function Layout({ children }) {
  const [more, setMore] = useState(false);
  const loc = useLocation();
  // Start each new screen at the top, except manual jumps to a chapter
  useEffect(() => {
    if (!new URLSearchParams(loc.search).get("topic")) window.scrollTo(0, 0);
  }, [loc.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  const current = loc.pathname.startsWith("/help") ? { label: "User manual" } : NAV.find((n) => (n.to === "/" ? loc.pathname === "/" : loc.pathname.startsWith(n.to)));
  const mobileMain = NAV.slice(0, 4);
  return (
    <div className="min-h-screen">
      <aside className="hidden md:flex fixed inset-y-0 left-0 w-64 flex-col border-r bg-card/80 backdrop-blur-md z-40">
        <div className="h-16 flex items-center gap-2.5 px-5 border-b">
          <img src="/icon.svg" alt="" className="h-8 w-8" />
          <div>
            <p className="font-display font-extrabold leading-tight">Household Budget</p>
            <p className="text-[11px] text-muted-foreground">Private · on your device</p>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-3 space-y-1" data-testid="sidebar-nav">
          {NAV.map((n) => <NavItem key={n.to} item={n} />)}
        </nav>
        <div className="p-3 border-t">
          <NavItem item={{ to: "/help", label: "User manual", icon: HelpCircle, id: "help" }} />
        </div>
      </aside>

      <header className="fixed top-0 right-0 left-0 md:left-64 h-14 md:h-16 border-b bg-background/85 backdrop-blur-lg z-30 flex items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-2 md:hidden">
          <img src="/icon.svg" alt="" className="h-7 w-7" />
          <span className="font-display font-bold">{current?.short || current?.label || "Budget"}</span>
        </div>
        <span className="hidden md:block text-sm text-muted-foreground">{current?.label}</span>
        <TopActions />
      </header>

      <main className="md:pl-64 pt-14 md:pt-16 pb-40 md:pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-6 sm:py-10">{children}</div>
      </main>

      <nav className="md:hidden fixed bottom-0 inset-x-0 border-t bg-card/95 backdrop-blur-xl z-50 pb-safe" data-testid="mobile-bottom-nav">
        <div className="h-16 flex items-center justify-around px-1">
          {mobileMain.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${n.id}`} className={({ isActive }) => cn("flex flex-col items-center gap-0.5 px-2 py-1 rounded-lg text-[11px] font-medium", isActive ? "text-primary" : "text-muted-foreground")}>
                <Icon className="h-5 w-5" />
                {n.short || n.label}
              </NavLink>
            );
          })}
          <button onClick={() => setMore(true)} className="flex flex-col items-center gap-0.5 px-2 py-1 text-[11px] font-medium text-muted-foreground" data-testid="mobile-nav-more">
            <MoreHorizontal className="h-5 w-5" /> More
          </button>
        </div>
      </nav>
      <Sheet open={more} onOpenChange={setMore}>
        <SheetContent side="bottom" className="rounded-t-2xl">
          <SheetHeader>
            <SheetTitle>More</SheetTitle>
          </SheetHeader>
          <div className="grid gap-1 mt-3">
            {[...NAV.slice(4), { to: "/help", label: "User manual", icon: HelpCircle, id: "help" }].map((n) => <NavItem key={n.to} item={n} onClick={() => setMore(false)} />)}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}
